import { useTranslation } from "react-i18next";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArchiveIcon from "@mui/icons-material/Archive";
import EditIcon from "@mui/icons-material/Edit";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import type { LimitationDefinition } from "src/shared/api-types/limitations";

import Button from "src/components/Button";

import { getDefinitionDisplayName } from "../limitationDisplay";
import LimitationPreviewChips from "./LimitationPreviewChips";

interface LimitationsTableProps {
  limitations: LimitationDefinition[];
  onEdit: (definition: LimitationDefinition) => void;
  onArchive: (definition: LimitationDefinition) => void;
  onUnarchive: (definition: LimitationDefinition) => void;
  onMove: (definition: LimitationDefinition, direction: -1 | 1) => void;
  onShowUsage: (definition: LimitationDefinition) => void;
}

const LimitationsTable = ({
  limitations,
  onEdit,
  onArchive,
  onUnarchive,
  onMove,
  onShowUsage,
}: LimitationsTableProps) => {
  const { t } = useTranslation();

  if (limitations.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
        }}
      >
        {t("limitations.no_configured")}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
        <colgroup>
          <col style={{ width: "22%" }} />
          <col style={{ width: "12%" }} />
          <col />
          <col style={{ width: 120 }} />
          <col style={{ width: 168 }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell>{t("limitations.name")}</TableCell>
            <TableCell align="center">{t("limitations.type")}</TableCell>
            <TableCell align="center">{t("limitations.values")}</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
              {t("limitations.usage")}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              {t("actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {limitations.map((definition, index) => (
            <TableRow key={definition.id}>
              <TableCell>
                <Typography variant="body2">{getDefinitionDisplayName(definition, t)}</Typography>
              </TableCell>
              <TableCell align="center">{t(`limitations.types.${definition.type}`)}</TableCell>
              <TableCell align="center">
                <LimitationPreviewChips definition={definition} />
              </TableCell>
              <TableCell align="center" sx={{ whiteSpace: "nowrap", "& .MuiButton-root": { mr: 0 } }}>
                {definition.usageCount > 0 ? (
                  <Button size="small" onClick={() => onShowUsage(definition)}>
                    {t("limitations.in_use", { count: definition.usageCount })}
                  </Button>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {t("limitations.not_used")}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ "& .MuiIconButton-root": { p: 0.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    icon={<ArrowUpwardIcon />}
                    title={t("limitations.move_up")}
                    onClick={() => onMove(definition, -1)}
                    disabled={index === 0}
                  />
                  <Button
                    icon={<ArrowDownwardIcon />}
                    title={t("limitations.move_down")}
                    onClick={() => onMove(definition, 1)}
                    disabled={index === limitations.length - 1}
                  />
                  <Button
                    icon={<EditIcon />}
                    title={t("limitations.edit_limitation")}
                    onClick={() => onEdit(definition)}
                  />
                  {definition.archived ? (
                    <Button
                      icon={<UnarchiveIcon />}
                      title={t("limitations.unarchive_limitation")}
                      onClick={() => onUnarchive(definition)}
                    />
                  ) : (
                    <Button
                      icon={<ArchiveIcon />}
                      title={t("limitations.archive_limitation")}
                      onClick={() => onArchive(definition)}
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LimitationsTable;
